import React, { useCallback, useEffect, useState } from "react";
import { Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import apiCall from "../../API/API";
import ExportButtons, { collectPages } from "../../Component/ExportButtons/ExportButtons";
import "../PlanSettings/PlanSettings.css";
import "../ActivationRequests/ActivationRequests.css";
import "./OldUsers.css";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const day = (d) => (d ? moment(d).format("DD MMM YYYY") : "");

/** Asks before restoring; lists who comes back. */
const RestoreModal = ({ users, onClose, onDone }) => {
  const [saving, setSaving] = useState(false);
  const list = users || [];

  const submit = async () => {
    setSaving(true);
    const { data, error } = await apiCall("POST", "/old-users/restore", { uids: list.map((u) => u.uid) });
    setSaving(false);
    if (error) return toast.error(error.message || "Could not restore.", { autoClose: 8000 });
    toast.success(data?.message || "Restored.", { autoClose: 6000 });
    onDone();
  };

  return (
    <Modal show={!!users} onHide={saving ? undefined : onClose} centered className="plan-settings-modal">
      <Modal.Header closeButton={!saving}>
        <Modal.Title>Restore {list.length === 1 ? "user" : `${list.length} users`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="ou-restore-list">
          {list.slice(0, 50).map((u) => (
            <span key={u.uid} className="ou-chip">
              {u.username}
              {u.name ? <small> {u.name}</small> : null}
            </span>
          ))}
          {list.length > 50 && <span className="ps-muted ps-small">and {list.length - 50} more</span>}
        </div>
        <ul className="ou-notes">
          <li>They come back with everything they had: packages, wallet, KYC, bank details and history.</li>
          <li>A sponsor above them that is an old user too comes back with them, so the team stays together.</li>
          <li>Team counts and ranks are worked out again.</li>
        </ul>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button className="ar-primary-btn" onClick={submit} disabled={saving || !list.length}>
          {saving ? <Spinner size="sm" /> : `Restore ${list.length === 1 ? "" : list.length}`.trim()}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/** Old users: members moved out of the system; the admin restores one or many. */
const OldUsers = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [applied, setApplied] = useState("");
  const [status, setStatus] = useState("");
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(() => new Map());
  const [confirm, setConfirm] = useState(null);

  const query = useCallback(
    (p, l) => {
      const params = new URLSearchParams({ page: p, limit: l });
      if (applied) params.set("search", applied);
      if (status) params.set("status", status);
      return `/old-users?${params.toString()}`;
    },
    [applied, status]
  );

  const load = useCallback(async () => {
    setLoading(true);
    const { data: res, error } = await apiCall("GET", query(page, limit));
    setLoading(false);
    if (error) return toast.error(error.message || "Could not load old users.");
    setData(res?.data || null);
  }, [query, page, limit]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setApplied(search.trim());
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const pages = data?.pages || 1;
  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.uid));

  const toggle = (r) =>
    setSelected((cur) => {
      const next = new Map(cur);
      if (next.has(r.uid)) next.delete(r.uid);
      else next.set(r.uid, r);
      return next;
    });

  const togglePage = () =>
    setSelected((cur) => {
      const next = new Map(cur);
      rows.forEach((r) => (allOnPage ? next.delete(r.uid) : next.set(r.uid, r)));
      return next;
    });

  const done = () => {
    setConfirm(null);
    setSelected(new Map());
    load();
  };

  return (
    <div className="plan-settings activation-requests-page old-users-page">
      <div className="ps-header">
        <div>
          <h2>Old Users</h2>
          <p>Members moved out of the system with all their data. Select one or many and restore them.</p>
        </div>
        <ExportButtons
          title="Old Users"
          disabled={!total}
          filters={[status ? `status: ${status}` : "", applied ? `search: ${applied}` : ""].filter(Boolean).join(", ")}
          columns={[
            { header: "Uid", value: (r) => r.uid },
            { header: "Username", value: (r) => r.username },
            { header: "Name", value: (r) => r.name },
            { header: "Email", value: (r) => r.email },
            { header: "Mobile", value: (r) => r.mobile },
            { header: "Status", value: (r) => (r.status === 1 ? "Active" : "Inactive") },
            { header: "Packages", value: (r) => r.packages },
            { header: "Investment", value: (r) => r.investment },
            { header: "Rank", value: (r) => r.rank },
            { header: "Sponsor", value: (r) => (r.sponsor ? `${r.sponsor.username}${r.sponsor.old ? " (old user)" : ""}` : "") },
            { header: "Joined", value: (r) => day(r.joining_date) },
            { header: "Activated", value: (r) => day(r.activation_date) },
            { header: "Moved on", value: (r) => day(r.archived_at) },
          ]}
          getRows={() =>
            collectPages(async (p, l) => {
              const { data: res } = await apiCall("GET", query(p, l));
              return { rows: res?.data?.rows || [], pages: res?.data?.pages || 1 };
            }, { limit: 500 })
          }
        />
      </div>

      <div className="ps-stats ar-stats">
        <div className="ps-stat ar-stat">
          <span>Old users</span>
          <strong>{data ? data.summary?.total ?? 0 : "—"}</strong>
          <small>moved out</small>
        </div>
        <div className="ps-stat ar-stat ar-stat--approved">
          <span>Were active</span>
          <strong>{data ? data.summary?.active ?? 0 : "—"}</strong>
          <small>had a package</small>
        </div>
        <div className={`ps-stat ar-stat ${selected.size ? "ar-stat--pending" : ""}`}>
          <span>Selected</span>
          <strong>{selected.size}</strong>
          <small>to restore</small>
        </div>
      </div>

      <div className="ps-panel" style={{ borderTop: "1px solid var(--ps-border)", borderRadius: "var(--ps-radius)" }}>
        <div className="ar-filters ou-filters">
          <div className="ar-f-search">
            <Form.Label>Search</Form.Label>
            <Form.Control size="sm" placeholder="Username, name, email or mobile" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div>
            <Form.Label>Status</Form.Label>
            <Form.Select size="sm" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Form.Select>
          </div>
          <div>
            <Form.Label>Per page</Form.Label>
            <Form.Select size="sm" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}>
              {[20, 50, 100, 200].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </Form.Select>
          </div>
          <div className="ou-actions">
            {selected.size > 0 && (
              <Button size="sm" variant="outline-secondary" onClick={() => setSelected(new Map())}>Clear selection</Button>
            )}
            <Button size="sm" className="ar-primary-btn" disabled={!selected.size} onClick={() => setConfirm([...selected.values()])}>
              Restore selected{selected.size ? ` (${selected.size})` : ""}
            </Button>
          </div>
        </div>

        <div className="ps-table-wrap">
          <Table className="ps-table">
            <thead>
              <tr>
                <th className="ou-check">
                  <Form.Check type="checkbox" checked={allOnPage} onChange={togglePage} disabled={!rows.length} aria-label="Select this page" />
                </th>
                <th>Member</th>
                <th>Email / Mobile</th>
                <th>Status</th>
                <th>Packages</th>
                <th>Sponsor</th>
                <th>Joined</th>
                <th className="text-end"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="ps-empty"><Spinner size="sm" /> Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="ps-empty">{applied || status ? "Nothing matches these filters." : "There are no old users."}</td></tr>
              )}
              {!loading &&
                rows.map((r) => (
                  <tr key={r.uid} className={selected.has(r.uid) ? "ou-selected" : ""}>
                    <td className="ou-check">
                      <Form.Check type="checkbox" checked={selected.has(r.uid)} onChange={() => toggle(r)} aria-label={`Select ${r.username}`} />
                    </td>
                    <td>
                      <div className="ps-strong">{r.name || "—"}</div>
                      <div className="ps-muted ps-small">{r.username} · uid {r.uid}</div>
                    </td>
                    <td className="ps-small">
                      <div>{r.email || "—"}</div>
                      <div className="ps-muted">{r.mobile || ""}</div>
                    </td>
                    <td>
                      <span className={`ou-status ${r.status === 1 ? "ou-status--active" : ""}`}>{r.status === 1 ? "Active" : "Inactive"}</span>
                      {r.rank && <div className="ps-muted ps-small">{r.rank}</div>}
                    </td>
                    <td className="ps-small">
                      {r.packages ? (
                        <>
                          <div className="ps-strong">{inr(r.investment)}</div>
                          <div className="ps-muted">{r.packages} package{r.packages === 1 ? "" : "s"}{r.activation_date ? ` · ${day(r.activation_date)}` : ""}</div>
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="ps-small">
                      {r.sponsor ? (
                        <>
                          {r.sponsor.username}
                          {r.sponsor.old && <div className="ps-muted">old user, comes back too</div>}
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="ps-small">{day(r.joining_date) || "—"}</td>
                    <td className="text-end">
                      <Button size="sm" variant="outline-primary" onClick={() => setConfirm([r])}>Restore</Button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>

        <div className="ar-pagination">
          <div className="ar-pagination-info">
            Showing <strong>{total ? (page - 1) * limit + 1 : 0}–{Math.min(page * limit, total)}</strong> of <strong>{total}</strong>
          </div>
          <div className="ar-pages">
            <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <span className="ps-muted ps-small">Page {page} of {pages}</span>
            <Button size="sm" variant="outline-secondary" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      </div>

      <RestoreModal users={confirm} onClose={() => setConfirm(null)} onDone={done} />
    </div>
  );
};

export default OldUsers;
